import * as React from 'react';
import {
  UsePopoverCloseEventsOptions,
  UsePopoverCloseEventsReturn,
} from './index.types';

/**
 * Hook to manage popover state
 * Automatically closes on scroll, resize, panel hide, or when trigger element leaves viewport
 */
export const usePopoverCloseEvents = ({
  onClose,
  panelRef,
  isSecondaryMenuVisible,
  triggerRef,
}: UsePopoverCloseEventsOptions = {}): UsePopoverCloseEventsReturn => {
  const [isOpen, setIsOpen] = React.useState(false);
  const onCloseRef = React.useRef(onClose);
  const preventAutoFocusRef = React.useRef(false);

  // Update ref when onClose changes to avoid re-creating the close function
  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const close = React.useCallback(() => {
    setIsOpen(false);
    onCloseRef.current?.();
  }, []);

  const open = React.useCallback(() => {
    setIsOpen(true);
    preventAutoFocusRef.current = false;
  }, []);

  // Handle outside events (scroll, resize, drag, viewport intersection)
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClose = () => {
      preventAutoFocusRef.current = true;
      // Force immediate visual hide
      setIsOpen(false);
      // Delay the actual close callback
      setTimeout(() => {
        close();
      }, 0);
    };

    const handleResize = () => {
      const panel = panelRef?.current;
      if (!panel) {
        handleClose();
        return;
      }

      const { display, visibility } = window.getComputedStyle(panel);
      if (display === 'none' || visibility === 'hidden') {
        handleClose();
      }
    };

    window.addEventListener('conversation-list-scroll', handleClose);
    window.addEventListener('file-drag-start', handleClose);
    window.addEventListener('resize', handleResize);

    // Observer for trigger element exiting viewport
    let observer: IntersectionObserver | undefined;
    if (triggerRef?.current) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              handleClose();
            }
          });
        },
        { root: null, threshold: 0 }
      );
      observer.observe(triggerRef.current);
    }

    return () => {
      window.removeEventListener('conversation-list-scroll', handleClose);
      window.removeEventListener('file-drag-start', handleClose);
      window.removeEventListener('resize', handleResize);
      observer?.disconnect();
    };
  }, [isOpen, close, panelRef, triggerRef]);

  // Handle prop-based closing
  React.useEffect(() => {
    if (isOpen && isSecondaryMenuVisible === false) {
      close();
    }
  }, [isOpen, isSecondaryMenuVisible, close]);

  return {
    isOpen,
    open,
    close,
    preventAutoFocusRef,
  };
};
